import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  School,
  ArrowLeft,
  Download,
  Printer,
  User,
  Calendar,
  Award,
  TrendingUp,
  BookOpen,
  AlertCircle,
  CheckCircle2,
  FileText,
  Clock,
  XCircle,
  UserCheck
} from 'lucide-react';
import apiService from '@/services/api';
import SecondaryReportCard from '@/components/SecondaryReportCard';
import PrimaryReportCard from '@/components/PrimaryReportCard';

export default function ReportCardViewerPage() {
  const { year, term, studentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [reportData, setReportData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSecondaryReportCard, setShowSecondaryReportCard] = useState(false);
  const [showPrimaryReportCard, setShowPrimaryReportCard] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState({ principal_name: '', address: '', phone: '' });

  // Check if school is secondary (St Patrick's College, Ambaebulu Junior Secondary, etc.)
  const isSecondarySchool = (schoolName) => {
    if (!schoolName) return false;
    const secondaryKeywords = ['college', 'secondary', 'junior secondary', 'st patrick', 'spc'];
    return secondaryKeywords.some(keyword => 
      schoolName.toLowerCase().includes(keyword)
    );
  };

  // Check if year level is secondary (Year 7+)
  const isSecondaryYear = (yearLevel) => {
    const year = parseInt(yearLevel);
    return year >= 7;
  };

  useEffect(() => {
    // Check if accessed by principal (via dashboard route with studentId param)
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const isPrincipal = currentUser?.role === 'principal' || currentUser?.role === 'admin';
    
    const fetchReportCard = async () => {
      try {
        setLoading(true);
        
        if (isPrincipal && studentId) {
          // Principal accessing student report - use authenticated API
          const yearVal = searchParams.get('year') || '2025';
          const termVal = searchParams.get('term') || '1';
          
          // Use the authenticated report card API
          const response = await apiService.getStudentReportCard(studentId, parseInt(yearVal), `Term ${termVal}`);
          setReportData(response);
          setStudentData({ id: studentId, name: response.student_name, school_id: currentUser?.school_id });
          
          // Fetch school info for principal name and address
          if (currentUser?.school_id) {
            try {
              const token = localStorage.getItem('authToken');
              const schoolRes = await fetch(`/api/schools/${currentUser.school_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (schoolRes.ok) {
                const data = await schoolRes.json();
                setSchoolInfo({
                  principal_name: data.principal_name || '',
                  address: data.address || '',
                  phone: data.phone || ''
                });
                setStudentData((prev) => ({ ...prev, school_name: data.name, school_id: data.id }));
              }
            } catch (e) { console.warn('Could not fetch school info:', e); }
          }
        } else {
          // Parent access (original flow)
          const token = localStorage.getItem('parentToken');
          const student = localStorage.getItem('studentData');

          if (!token || !student) {
            navigate('/parent/login');
            return;
          }

          const studentInfo = JSON.parse(student);
          setStudentData(studentInfo);
          
          const response = await apiService.getParentReportCard(token, studentInfo.id, parseInt(year), term);
          setReportData(response);
          
          // Fetch school info for parent view
          if (studentInfo.school_id) {
            try {
              const schoolRes = await fetch(`/api/schools/${studentInfo.school_id}`);
              if (schoolRes.ok) {
                const data = await schoolRes.json();
                setSchoolInfo({
                  principal_name: data.principal_name || '',
                  address: data.address || '',
                  phone: data.phone || ''
                });
              }
            } catch (e) { console.warn('Could not fetch school info:', e); }
          }
        }
      } catch (err) {
        console.error('Error fetching report card:', err);
        setError('Failed to load report card. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportCard();
  }, [year, term, studentId, navigate, searchParams]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // For now, trigger print which can save as PDF
    window.print();
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'bg-green-600';
      case 'B': return 'bg-blue-600';
      case 'C': return 'bg-yellow-600';
      case 'D': return 'bg-orange-600';
      case 'F': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  const getPerformanceLevel = (percentage) => {
    if (percentage >= 80) return { label: 'Excellent', color: 'text-green-600' };
    if (percentage >= 65) return { label: 'Very Good', color: 'text-blue-600' };
    if (percentage >= 50) return { label: 'Good', color: 'text-yellow-600' };
    if (percentage >= 35) return { label: 'Satisfactory', color: 'text-orange-600' };
    return { label: 'Needs Improvement', color: 'text-red-600' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Report Card...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <div className="max-w-4xl mx-auto mt-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/parent/dashboard')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  const performance = getPerformanceLevel(reportData.overall_percentage);
  
  // Determine if this is a secondary school student
  const isSecondary = isSecondarySchool(studentData?.school_name) || isSecondaryYear(reportData.year);
  const isPrimary = !isSecondary;
  
  // For primary school, transform data for PrimaryReportCard
  const getPrimarySubjects = () => {
    if (!reportData.subjects) return [];
    return reportData.subjects.map(subject => ({
      name: subject.subject,
      ca40: subject.assessments ? 
        (() => {
          let totalCA = 0, totalMaxCA = 0;
          subject.assessments.forEach(a => {
            if (!a.title?.toLowerCase().includes('exam')) {
              totalCA += a.marks_obtained || 0;
              totalMaxCA += a.max_marks || 0;
            }
          });
          return totalMaxCA > 0 ? (totalCA / totalMaxCA) * 40 : 0;
        })() : 0,
      exam60: subject.assessments ?
        (() => {
          let totalExam = 0, totalMaxExam = 0;
          subject.assessments.forEach(a => {
            if (a.title?.toLowerCase().includes('exam')) {
              totalExam += a.marks_obtained || 0;
              totalMaxExam += a.max_marks || 0;
            }
          });
          return totalMaxExam > 0 ? (totalExam / totalMaxExam) * 60 : 0;
        })() : 0,
      rank: subject.ranking ? `${subject.ranking}/${subject.total_students || '?'}` : '-',
      assessments: subject.assessments || []
    }));
  };
  
  // Transform report data to secondary format for the dialog
  const getSecondaryGrades = () => {
    if (!reportData.subjects) return [];
    return reportData.subjects.map(subject => ({
      name: subject.subject,
      ca: subject.ca_marks || 0,
      exam: subject.exam_marks || 0,
      total: Math.round(subject.percentage) || 0,
      classAvg: subject.class_average || 0,
      ranking: subject.ranking || 0,
      absences: subject.absences || 0,
      teacherComment: subject.teacher_comment || ''
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Secondary Report Card Dialog */}
      <SecondaryReportCard
        isOpen={showSecondaryReportCard}
        onClose={() => setShowSecondaryReportCard(false)}
        student={{
          name: reportData.student_name,
          student_id: studentData?.student_id || studentData?.id,
          id: studentData?.id
        }}
        classInfo={{
          name: `Year ${reportData.year} ${reportData.stream || ''}`.trim()
        }}
        grades={getSecondaryGrades()}
        schoolName={studentData?.school_name || "St Patrick's College"}
        term={reportData.term || `Term ${term}`}
        year={parseInt(year) || 2025}
        schoolAddress={(() => {
          const sn = studentData?.school_name || '';
          const addr = schoolInfo.address || '';
          if (sn.toLowerCase().includes("patrick")) {
            return {
              poBox: "PO BOX 19",
              location: addr || "Nawolu, Vureas, North East Ambae",
              country: "VANUATU",
              email: "spc.secretaryoffice@gmail.com"
            };
          }
          return {
            poBox: "",
            location: addr || "Penama Province",
            country: "VANUATU",
            email: ""
          };
        })()}
        classTeacherComment={reportData.class_teacher_comment || ''}
        deputyPrincipalComment={reportData.deputy_principal_comment || ''}
        principalComment={reportData.principal_comment || ''}
      />

      {/* For Primary Schools - show same report card as teacher */}
      {isPrimary && (
        <PrimaryReportCard
          isOpen={true}
          onClose={() => navigate('/parent/dashboard')}
          student={{
            name: reportData.student_name,
            birth_registration: reportData.birth_registration || studentData?.birth_registration,
            student_id: studentData?.student_id || studentData?.id,
            id: studentData?.id
          }}
          classInfo={{ name: `Year ${reportData.year || year}`, year: reportData.year || parseInt(year) }}
          subjects={getPrimarySubjects()}
          schoolName={studentData?.school_name || ''}
          schoolId={studentData?.school_id || ''}
          schoolPhone={schoolInfo.phone || ''}
          schoolAddress={schoolInfo.address || ''}
          term={reportData.term || `Term ${term}`}
          year={2026}
          principalName={schoolInfo.principal_name || ''}
          reportData={reportData}
          assessmentMarks={{}}
        />
      )}

      {/* For Secondary Schools - show existing view */}
      {!isPrimary && (
        <>
      {/* Header - No Print */}
      <header className="bg-white shadow-md sticky top-0 z-10 print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/parent/dashboard')}
              variant="ghost"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="flex gap-2">
              {isSecondary && (
                <Button 
                  onClick={() => setShowSecondaryReportCard(true)} 
                  variant="outline" 
                  className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <FileText className="h-4 w-4" />
                  SPC Format
                </Button>
              )}
              <Button onClick={handlePrint} variant="outline" className="gap-2">
                <Printer className="h-4 w-4" />
                Print
              </Button>
              <Button onClick={handleDownload} className="gap-2">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 print:px-8">
        {/* School Header - Print Friendly */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-6 print:shadow-none">
          <div className="text-center mb-8 border-b-2 border-gray-200 pb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <School className="h-10 w-10 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">{studentData?.school_name || 'Penama Education Portal'}</h1>
            </div>
            <p className="text-gray-600 text-lg mb-2">Penama Province, Vanuatu</p>
            <p className="text-sm text-gray-500 italic">&quot;Educating The Whole Person&quot;</p>
          </div>

          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ACADEMIC REPORT CARD
            </h2>
            <p className="text-lg text-gray-700">
              {term} - Year {year}
            </p>
          </div>

          {/* Student Information */}
          <div className="grid grid-cols-2 gap-6 mb-6 bg-gray-50 p-6 rounded-lg">
            <div>
              <p className="text-sm text-gray-600 mb-1">Student Name</p>
              <p className="font-bold text-lg">{reportData.student_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Year Level</p>
              <p className="font-bold text-lg">Year {reportData.year}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Stream</p>
              <p className="font-bold text-lg">{reportData.stream || 'Not Assigned'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Term</p>
              <p className="font-bold text-lg">{reportData.term}</p>
            </div>
          </div>

          {/* Overall Performance Summary */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg mb-8">
            <h3 className="text-xl font-bold mb-4 text-center">Overall Performance Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <BookOpen className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm opacity-90 mb-1">Total Subjects</p>
                <p className="text-3xl font-bold">{reportData.total_subjects}</p>
              </div>
              <div className="text-center">
                <Award className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm opacity-90 mb-1">Overall GPA</p>
                <p className="text-3xl font-bold">{reportData.overall_gpa.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm opacity-90 mb-1">Overall Percentage</p>
                <p className="text-3xl font-bold">{reportData.overall_percentage.toFixed(1)}%</p>
              </div>
              <div className="text-center">
                <Award className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm opacity-90 mb-1">Overall Grade</p>
                <p className="text-3xl font-bold">{reportData.overall_grade || 'N/A'}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/30">
              <div className="text-center">
                <p className="text-sm opacity-90 mb-1">Total Marks</p>
                <p className="text-2xl font-bold">{reportData.total_marks_obtained || 0} / {reportData.total_marks_possible || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm opacity-90 mb-1">Performance Level</p>
                <p className="text-2xl font-bold">{performance.label}</p>
              </div>
            </div>
          </div>

          {reportData.promotion_decision && (
            <div className={`rounded-lg border-2 p-5 mb-8 text-center print:break-inside-avoid ${reportData.promotion_decision.decision === 'pass' ? 'bg-emerald-50 border-emerald-400' : 'bg-amber-50 border-amber-400'}`} data-testid="promotion-decision-banner">
              <p className="text-xs uppercase tracking-wide text-gray-600 mb-1">End of year decision</p>
              <p className={`text-2xl font-bold ${reportData.promotion_decision.decision === 'pass' ? 'text-emerald-800' : 'text-amber-800'}`}>
                {reportData.promotion_decision.decision === 'pass' ? `Promoted to Year ${reportData.promotion_decision.next_year}` : `Repeat Year ${reportData.promotion_decision.year}`}
              </p>
            </div>
          )}

          {/* Attendance Record */}
          {reportData.attendance && (
            <Card className="shadow-lg print:shadow-none print:break-inside-avoid mb-8">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-emerald-600" />
                  Attendance Record
                </CardTitle>
                <CardDescription>
                  Student attendance summary for this term
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
                    <p className="text-3xl font-bold text-emerald-700">{reportData.attendance.present || 0}</p>
                    <p className="text-sm text-emerald-600 font-medium">Days Present</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                    <XCircle className="h-8 w-8 mx-auto mb-2 text-red-600" />
                    <p className="text-3xl font-bold text-red-700">{reportData.attendance.absent || 0}</p>
                    <p className="text-sm text-red-600 font-medium">Days Absent</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Clock className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                    <p className="text-3xl font-bold text-amber-700">{reportData.attendance.late || 0}</p>
                    <p className="text-sm text-amber-600 font-medium">Days Late</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-3xl font-bold text-blue-700">{reportData.attendance.total_days || 0}</p>
                    <p className="text-sm text-blue-600 font-medium">Total Days</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <TrendingUp className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                    <p className="text-3xl font-bold text-purple-700">{reportData.attendance.attendance_rate?.toFixed(1) || 0}%</p>
                    <p className="text-sm text-purple-600 font-medium">Attendance Rate</p>
                  </div>
                </div>
                {/* Attendance Progress Bar */}
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Attendance Rate</span>
                    <Badge className={
                      (reportData.attendance.attendance_rate || 0) >= 90 ? 'bg-emerald-100 text-emerald-800' :
                      (reportData.attendance.attendance_rate || 0) >= 80 ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {(reportData.attendance.attendance_rate || 0) >= 90 ? 'Excellent' :
                       (reportData.attendance.attendance_rate || 0) >= 80 ? 'Good' : 'Needs Improvement'}
                    </Badge>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div 
                      className={`h-4 rounded-full transition-all duration-500 ${
                        (reportData.attendance.attendance_rate || 0) >= 90 ? 'bg-emerald-500' :
                        (reportData.attendance.attendance_rate || 0) >= 80 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(reportData.attendance.attendance_rate || 0, 100)}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Subject-wise Performance - Summary Table */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Subject Performance Summary</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border-2 border-gray-300">
              <thead className="bg-gradient-to-r from-blue-100 to-indigo-100">
                <tr>
                  <th className="px-4 py-3 text-left font-bold border-2 border-gray-300 text-gray-800">SUBJECT</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">CA (40%)</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">EXAM (60%)</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">TOTAL</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">PERCENTAGE</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">GRADE</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">GPA</th>
                  <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-800">RANKING</th>
                  <th className="px-4 py-3 text-left font-bold border-2 border-gray-300 text-gray-800">TEACHER</th>
                  <th className="px-4 py-3 text-left font-bold border-2 border-gray-300 text-gray-800">TEACHER COMMENT</th>
                </tr>
              </thead>
              <tbody>
                {reportData.subjects.map((subject, index) => (
                  <tr key={index} className={`border-2 border-gray-300 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                    <td className="px-4 py-3 border-2 border-gray-300 font-semibold text-gray-800">{subject.subject}</td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300">
                      <div className="font-medium">{subject.ca_marks || 0}/{subject.ca_max || 0}</div>
                      <div className="text-xs text-gray-600">{subject.ca_percentage ? subject.ca_percentage.toFixed(1) : '0.0'}%</div>
                    </td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300">
                      <div className="font-medium">{subject.exam_marks || 0}/{subject.exam_max || 0}</div>
                      <div className="text-xs text-gray-600">{subject.exam_percentage ? subject.exam_percentage.toFixed(1) : '0.0'}%</div>
                    </td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300 font-bold">
                      {subject.total_marks_obtained}/{subject.total_marks_possible}
                    </td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300 font-semibold text-blue-700">
                      {subject.percentage.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300">
                      <Badge className={`${getGradeColor(subject.grade)} text-white px-3 py-1`}>
                        {subject.grade}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300 font-semibold">
                      {subject.gpa ? subject.gpa.toFixed(2) : '0.00'}
                    </td>
                    <td className="px-4 py-3 text-center border-2 border-gray-300 font-medium text-amber-700">
                      {subject.ranking || '-'}
                    </td>
                    <td className="px-4 py-3 border-2 border-gray-300 text-gray-700">{subject.teacher_name}</td>
                    <td className="px-4 py-3 border-2 border-gray-300 text-gray-700 text-sm italic">
                      {subject.teacher_comment || 'No comment available'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Subject-wise Performance */}
        <div className="space-y-4 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Detailed Subject Breakdown</h3>
          
          {reportData.subjects.map((subject, index) => (
            <Card key={index} className="shadow-lg print:shadow-none print:break-inside-avoid">
              <CardHeader className="bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      {subject.subject}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Teacher: {subject.teacher_name}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <Badge className={`${getGradeColor(subject.grade)} text-white text-lg px-4 py-1`}>
                      {subject.grade}
                    </Badge>
                    <p className="text-sm text-gray-600 mt-1">
                      {subject.percentage.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Summary Table */}
                <div className="mb-4 overflow-x-auto">
                  <table className="w-full text-sm border-collapse border-2 border-gray-300">
                    <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold border-2 border-gray-300 text-gray-700">Component</th>
                        <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-700">Marks</th>
                        <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-700">Percentage</th>
                        <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-700">Grade</th>
                        <th className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-gray-700">GPA</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-2 border-gray-300 hover:bg-blue-50 transition-colors">
                        <td className="px-4 py-3 border-2 border-gray-300">
                          <span className="font-bold text-blue-700">CA Total</span>
                          <span className="text-xs text-blue-600 ml-2 font-semibold">(40%)</span>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold border-2 border-gray-300">
                          {subject.ca_marks || 0}/{subject.ca_max || 0}
                        </td>
                        <td className="px-4 py-3 text-center font-semibold border-2 border-gray-300">
                          {subject.ca_percentage ? subject.ca_percentage.toFixed(1) : '0.0'}%
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300">
                          <Badge className="bg-blue-600 text-white px-3 py-1">{subject.ca_grade || '-'}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300 text-gray-500">-</td>
                      </tr>
                      <tr className="border-2 border-gray-300 hover:bg-purple-50 transition-colors">
                        <td className="px-4 py-3 border-2 border-gray-300">
                          <span className="font-bold text-purple-700">Exam</span>
                          <span className="text-xs text-purple-600 ml-2 font-semibold">(60%)</span>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold border-2 border-gray-300">
                          {subject.exam_marks || 0}/{subject.exam_max || 0}
                        </td>
                        <td className="px-4 py-3 text-center font-semibold border-2 border-gray-300">
                          {subject.exam_percentage ? subject.exam_percentage.toFixed(1) : '0.0'}%
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300">
                          <Badge className="bg-purple-600 text-white px-3 py-1">{subject.exam_grade || '-'}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300 text-gray-500">-</td>
                      </tr>
                      <tr className="border-2 border-gray-300 bg-gradient-to-r from-blue-100 to-indigo-100 font-bold">
                        <td className="px-4 py-3 border-2 border-gray-300">
                          <span className="font-bold text-indigo-900 text-base">TOTAL</span>
                          <span className="text-xs text-indigo-700 ml-2 font-semibold">(100%)</span>
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300 font-bold text-base">
                          {subject.total_marks_obtained}/{subject.total_marks_possible}
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300 font-bold text-base">
                          {subject.percentage.toFixed(1)}%
                        </td>
                        <td className="px-4 py-3 text-center border-2 border-gray-300">
                          <Badge className={`${getGradeColor(subject.grade)} text-white text-base px-4 py-1`}>
                            {subject.grade}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-base text-indigo-900">
                          {subject.gpa ? subject.gpa.toFixed(2) : '0.00'}
                        </td>
                      </tr>
                      <tr className="border-2 border-gray-300 bg-gradient-to-r from-yellow-100 to-amber-100">
                        <td className="px-4 py-3 border-2 border-gray-300 font-bold text-amber-900">Class Ranking</td>
                        <td className="px-4 py-3 text-center font-bold border-2 border-gray-300 text-lg text-amber-900" colSpan="4">
                          🏆 {subject.ranking || '-'} out of {subject.total_students || '-'} students
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Assessment Breakdown */}
                {subject.assessments && subject.assessments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Assessment Details:</p>
                    <div className="space-y-1">
                      {subject.assessments.map((assessment, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                          <span className="text-gray-700">{assessment.assessment_title}</span>
                          <span className="font-medium">
                            {assessment.marks_obtained}/{assessment.max_marks} 
                            <span className="text-gray-500 ml-2">({assessment.percentage.toFixed(1)}%)</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Grading Scale */}
        <Card className="shadow-lg print:shadow-none print:break-inside-avoid mb-8">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-lg">Grading Scale</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="text-center">
                <Badge className="bg-green-600 text-white w-full mb-2">A</Badge>
                <p className="text-xs text-gray-600">80-100%</p>
                <p className="text-xs text-gray-500">Excellent</p>
              </div>
              <div className="text-center">
                <Badge className="bg-blue-600 text-white w-full mb-2">B</Badge>
                <p className="text-xs text-gray-600">65-79%</p>
                <p className="text-xs text-gray-500">Very Good</p>
              </div>
              <div className="text-center">
                <Badge className="bg-yellow-600 text-white w-full mb-2">C</Badge>
                <p className="text-xs text-gray-600">50-64%</p>
                <p className="text-xs text-gray-500">Good</p>
              </div>
              <div className="text-center">
                <Badge className="bg-orange-600 text-white w-full mb-2">D</Badge>
                <p className="text-xs text-gray-600">35-49%</p>
                <p className="text-xs text-gray-500">Satisfactory</p>
              </div>
              <div className="text-center">
                <Badge className="bg-red-600 text-white w-full mb-2">F</Badge>
                <p className="text-xs text-gray-600">0-34%</p>
                <p className="text-xs text-gray-500">Needs Improvement</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Principal's Comment */}
        <Card className="shadow-lg print:shadow-none print:break-inside-avoid mb-8">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-600" />
              Principal&apos;s Comment
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {reportData.principal_comment ? (
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-indigo-600">
                <p className="text-gray-800 italic">&quot;{reportData.principal_comment}&quot;</p>
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-300">
                <p className="text-gray-500 italic">No comment has been added for this term yet.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="shadow-lg print:shadow-none print:break-inside-avoid mb-8">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-lg">Authorized Signatures</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Principal Signature */}
              <div className="text-center">
                <div className="mb-4 h-24 flex items-center justify-center border-b-2 border-gray-300">
                  {reportData.signatures?.principal ? (
                    <img 
                      src={reportData.signatures.principal} 
                      alt="Principal Signature" 
                      className="max-h-20 max-w-full object-contain"
                    />
                  ) : (
                    <div className="h-20 flex items-center justify-center">
                      <p className="text-gray-400 text-sm">Signature</p>
                    </div>
                  )}
                </div>
                <p className="font-semibold text-gray-800">Principal</p>
                <p className="text-sm text-gray-600">{studentData?.school_name || 'Penama Education Portal'}</p>
              </div>

              {/* Class Teacher Signature */}
              <div className="text-center">
                <div className="mb-4 h-24 flex items-center justify-center border-b-2 border-gray-300">
                  {reportData.signatures?.class_teacher ? (
                    <img 
                      src={reportData.signatures.class_teacher} 
                      alt="Class Teacher Signature" 
                      className="max-h-20 max-w-full object-contain"
                    />
                  ) : (
                    <div className="h-20 flex items-center justify-center">
                      <p className="text-gray-400 text-sm">Signature</p>
                    </div>
                  )}
                </div>
                <p className="font-semibold text-gray-800">Class Teacher</p>
                <p className="text-sm text-gray-600">Year {reportData.year}</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">Date Issued:</span> {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-lg p-6 print:shadow-none">
          <div className="text-center text-sm text-gray-600">
            <p className="mb-2">This is an official report card from {studentData?.school_name || 'Penama Education Portal'}</p>
            <p className="text-xs text-gray-500">Academic Year 2025-2026</p>
            <p className="text-xs text-gray-500 mt-2">
              Generated on {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>
      </main>
      </>
      )}
    </div>
  );
}
